import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useId } from 'react'
import { Button, cx } from './primitives'

/** Everything a control looks like, minus how tall it is. See .qh-field in index.css. */
const CONTROL_BOX =
  'w-full rounded-md border border-line bg-surface px-3 text-sm text-ink ' +
  'placeholder:text-faint focus:border-accent focus:outline-none ' +
  'disabled:bg-sunken disabled:text-muted'

const CONTROL = `${CONTROL_BOX} qh-field`

export function Field({
  label,
  hint,
  error,
  required,
  changed,
  row,
  children,
}: {
  label: string
  hint?: string
  error?: string
  required?: boolean
  /**
   * Edited and not yet saved. Marks which fields a Discard would put back, so the
   * control is answering a question the page has already shown the answer to.
   */
  changed?: boolean
  /**
   * Set by <see cref="FieldRow"/>. Hands the field's three parts to the row's own grid
   * so labels, controls and hints line up across every field beside it.
   *
   * A prop rather than a className the caller passes, because the two layouts set the
   * same property: two display utilities on one element are decided by their order in
   * the generated stylesheet, not by the order they are written in. Exactly one is
   * emitted here, so there is nothing to lose that race.
   */
  row?: boolean
  children: (id: string) => ReactNode
}) {
  const id = useId()

  return (
    <div
      className={cx(
        row ? 'row-span-3 grid grid-rows-subgrid gap-1.5' : 'flex flex-col gap-1.5',
        // Pulled out to the sides so the tint reads as a band across the field rather
        // than a box drawn around it, and the grid's own spacing is left alone.
        changed && '-mx-2 rounded-md bg-accent-wash px-2 py-1.5',
      )}
    >
      <label htmlFor={id} className="text-[13px] font-medium text-ink-2">
        {label}
        {required && <span className="ml-1 text-crit">*</span>}
        {changed && (
          <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.1em] text-accent">
            Edited
          </span>
        )}
      </label>
      {children(id)}
      {/* The hint disappears once there is an error: two lines of guidance under one
          box is noise at exactly the moment the person needs one clear sentence. */}
      {error ? (
        <p className="text-[12px] text-crit">{error}</p>
      ) : (
        hint && <p className="text-[12px] text-muted">{hint}</p>
      )}
    </div>
  )
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(CONTROL, className)} {...rest} />
}

export function MoneyInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="number"
      inputMode="decimal"
      step="0.01"
      className={cx(CONTROL, 'tabular text-right', className)}
      {...rest}
    />
  )
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={4} className={cx(CONTROL, 'resize-y', className)} {...rest} />
}

/**
 * A dropdown that looks like one.
 *
 * `appearance-none` removes the platform's own chevron, which is the point - the native
 * one differs per browser and ignores the type scale. It also means nothing indicates the
 * control opens unless we draw the arrow ourselves, which is what the background image is.
 * Its colour comes from `--chevron`, defined alongside `--muted` in every theme block.
 */
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cx(
        CONTROL_BOX,
        'qh-select appearance-none bg-[image:var(--chevron)] bg-[length:16px] bg-[right_0.625rem_center] bg-no-repeat pr-8',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  )
}

export function Checkbox({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input type="checkbox" className="size-4 accent-[var(--accent)]" {...rest} />
      {label}
    </label>
  )
}

/**
 * One standing setting, on or off.
 *
 * A checkbox would have done the same job. It reads as one of a set being ticked, which is
 * what every other box beside it is - a role, a country. This is a single exception granted
 * to one person, and it is drawn differently so it is not read as another of those.
 *
 * The label is a sibling of the control rather than a wrapper: a <label> forwards a click
 * to a form control and a button is not one, so wrapping it would leave the words dead.
 */
export function Switch({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
}) {
  const labelId = useId()

  return (
    <div className={cx('flex items-start justify-between gap-3', disabled && 'opacity-60')}>
      <span>
        <span id={labelId} className="block text-[13px] font-medium text-ink-2">
          {label}
        </span>
        {hint && <span className="mt-0.5 block text-[12px] text-muted">{hint}</span>}
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative mt-0.5 h-5 w-9 shrink-0 rounded-full border transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
          checked ? 'border-accent bg-accent' : 'border-line bg-sunken',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <span
          className={cx(
            'absolute top-[0.1875rem] size-3.5 rounded-full bg-surface transition-all',
            checked ? 'left-[1.125rem]' : 'left-[0.1875rem]',
          )}
        />
      </button>
    </div>
  )
}

/**
 * A row of fields that line up.
 *
 * Laying these out with `items-end` looked right until one field had a hint and its
 * neighbour did not: aligning the boxes' bottoms then pushed the shorter field's label
 * and control down by the height of a line of text, and the row read as a mistake. The
 * three parts of a field belong on three shared rows, which is what subgrid is for -
 * the label row is as tall as the tallest label, and every control sits on the same
 * line whatever is written underneath it.
 *
 * Anything in here that is not a Field - a checkbox, a button - should carry
 * `row-span-3 grid grid-rows-subgrid` and place itself on the row it belongs to,
 * rather than being nudged into place with a margin.
 */
export function FieldRow({ children }: { children: ReactNode }) {
  return (
    // Columns, never rows: the three parts of a field are the three rows, so an extra
    // field is another column. On a narrow screen that overflows, and the row scrolls
    // inside itself rather than pushing the page sideways.
    <div className="-mx-1 overflow-x-auto px-1 pb-1">
      <div className="grid w-max auto-cols-max grid-flow-col grid-rows-[auto_auto_auto] items-start gap-x-3">
        {children}
      </div>
    </div>
  )
}

export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-4 p-5 sm:grid-cols-2">{children}</div>
}

export function FormActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-end gap-2 border-t border-line-soft px-5 py-3">
      {children}
    </div>
  )
}

/**
 * The bar at the foot of an editable card: what is outstanding, and the two answers to it.
 *
 * The count is what makes Discard mean something. Without it the button is one somebody
 * presses hopefully; with it, it names work they can see marked on the fields above.
 * With nothing changed, both controls are disabled and the count disappears - the page is
 * then just a page.
 *
 * `children` is for controls that are not part of saving, such as a destructive action.
 */
export function SaveActions({
  count,
  saving,
  saveLabel,
  canSave = true,
  onSave,
  onDiscard,
  children,
}: {
  count: number
  saving: boolean
  saveLabel: string
  /** False while a required field is empty, so the round trip is not spent on a 400. */
  canSave?: boolean
  onSave: () => void
  onDiscard: () => void
  children?: ReactNode
}) {
  return (
    <FormActions>
      {count > 0 && (
        <span className="mr-auto text-[13px] text-muted" role="status">
          {count} unsaved change{count === 1 ? '' : 's'}
        </span>
      )}

      {children}

      <Button size="sm" disabled={count === 0 || saving} onClick={onDiscard}>
        Discard
      </Button>
      <Button size="sm" variant="primary" loading={saving} disabled={count === 0 || !canSave} onClick={onSave}>
        {saveLabel}
      </Button>
    </FormActions>
  )
}
